import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MdbModalModule, MdbSelectModule } from 'mdb5-adapted';
import { NgxPaginationModule } from 'ngx-pagination';
import { AppUtilityService } from 'src/app/app-utility.service';
import { PageCardheadComponent } from 'src/app/shared/ui/pageheader-card/pageheader-card.component';

import { LmsTableWrapperComponent } from '@lmsShared';
import { LMS_NoSpecialsDirective, NumbersOnlyDirective, UpperCaseInputDirective } from '@lmsSharedUI';

import { Employer, EmployerCorrectionRequest, EmployerCorrectionResult, EmployerCorrectionRow } from '../models/employer-correction.model';
import { CsvParserService } from '../services/csv-parser.service';
import { EmployerService } from '../services/employer.service';

const CSV_TEMPLATE_HEADER = 'serial,tnumber,name,correctedSpelling';

@Component({
  selector: 'lms-employer-list',
  templateUrl: './employer-list.component.html',
  styleUrls: ['./employer-list.component.scss'],
  standalone: true,
    imports: [
      CommonModule,
      FormsModule,
      ReactiveFormsModule,
      UpperCaseInputDirective,
      MdbModalModule,
      NgxPaginationModule,
      LmsTableWrapperComponent,
      NumbersOnlyDirective,
      LMS_NoSpecialsDirective,
      PageCardheadComponent,
      MdbSelectModule,
    ],
})
export class EmployerListComponent implements OnInit {
  rows: EmployerCorrectionRow[] = [];

  isLoading = false;
  isBulkUpdating = false;
  isCsvProcessing = false;
  errorMessage: string | null = null;
  skippedRowCount = 0;
  csvWarnings: string[] = [];
  searchText = '';
  loadMode: 'serial' | 'name' = 'serial';
  loadValue = '';

  config = {
    id: 'employerCorrectionPager',
    itemsPerPage: 10,
    currentPage: 1,
  };

  constructor(
    private employerService: EmployerService,
    private csvParserService: CsvParserService,
    public global_utility: AppUtilityService,
  ) { }

  ngOnInit(): void {
    this.loadValue = this.global_utility.getGlobalData('branch_serial') || '';
    this.loadEmployers();
  }

  loadEmployersByCriteria(): void {
    const value = this.loadValue.trim();
    if (!value) {
      this.errorMessage = `Enter an employer ${this.loadMode} to search.`;
      this.rows = [];
      return;
    }

    this.loadEmployers();
  }

  /** Rows matching the current search text, filtered by serial, TNR or name. */
  get filteredRows(): EmployerCorrectionRow[] {
    const term = this.searchText.trim().toLowerCase();
    if (!term) {
      return this.rows;
    }
    return this.rows.filter(row =>
      row.serial?.toLowerCase().includes(term) ||
      String(row.tnumber).includes(term) ||
      row.name?.toLowerCase().includes(term)
    );
  }

  get selectedRows(): EmployerCorrectionRow[] {
    return this.rows.filter(row => row.selected && row.status === 'modified');
  }

  get isAllSelected(): boolean {
    const selectable = this.filteredRows.filter(row => row.status === 'modified');
    return selectable.length > 0 && selectable.every(row => row.selected);
  }

  get canApplySelected(): boolean {
    return this.selectedRows.length > 0 && !this.isBulkUpdating;
  }

  get summary(): { total: number; succeeded: number; failed: number } {
    return {
      total: this.rows.length,
      succeeded: this.rows.filter(row => row.status === 'success').length,
      failed: this.rows.filter(row => row.status === 'failed').length,
    };
  }

  /** Trims/normalises the corrected name and refreshes the row's status. */
  onCorrectedNameChange(row: EmployerCorrectionRow): void {
    const trimmed = (row.correctedSpelling || '').trim();
    row.correctedSpelling = trimmed;

    if (trimmed === '' || trimmed === row.name) {
      row.status = 'unmodified';
      row.selected = false;
    } else {
      row.status = 'modified';
    }
    row.message = undefined;
  }

  toggleSelectAll(checked: boolean): void {
    this.filteredRows
      .filter(row => row.status === 'modified')
      .forEach(row => (row.selected = checked));
  }

  /** Reads and merges a CSV file (same columns as the table) into the grid. */
  onCsvFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    this.isCsvProcessing = true;
    this.csvWarnings = [];
    this.skippedRowCount = 0;

    this.csvParserService.parseFile(file).then(result => {
      this.skippedRowCount = result.skippedCount;
      this.csvWarnings = result.skippedWarnings;

      result.rows.forEach(csvRow => {
        const existing = this.rows.find(row => row.serial === csvRow.serial && row.tnumber === csvRow.tnumber);
        if (existing) {
          existing.correctedSpelling = csvRow.correctedSpelling;
          this.onCorrectedNameChange(existing);
        } else {
          const row: EmployerCorrectionRow = {
            id: `${csvRow.serial}-${csvRow.tnumber}`,
            serial: csvRow.serial,
            tnumber: csvRow.tnumber,
            name: csvRow.name,
            branchName: '',
            correctedSpelling: csvRow.correctedSpelling,
            status: 'unmodified',
            selected: false,
          };
          this.onCorrectedNameChange(row);
          this.rows.push(row);
        }
      });

      this.isCsvProcessing = false;
      input.value = '';
    }).catch(err => {
      this.errorMessage = err instanceof Error ? err.message : 'Failed to read CSV file';
      this.isCsvProcessing = false;
      input.value = '';
    });
  }

  /** Applies a single row's correction. */
  applyCorrection(row: EmployerCorrectionRow): void {
    if (row.status !== 'modified') {
      return;
    }

    row.status = 'updating';
    row.message = undefined;

    const payload: EmployerCorrectionRequest = {
      serial: row.serial,
      tnumber: row.tnumber,
      incorrectSpelling: row.name,
      correctedSpelling: row.correctedSpelling,
    };

    this.employerService.correctEmployer(payload).subscribe({
      next: (result: EmployerCorrectionResult) => this.applyResultToRow(row, result),
      error: (err) => this.applyErrorToRow(row, err),
    });
  }

  /** Confirms and applies corrections for all selected/modified rows. */
  applySelected(): void {
    const rows = this.selectedRows;
    if (rows.length === 0) {
      return;
    }

    const confirmed = window.confirm(
      `Apply ${rows.length} employer name correction(s)? This will update the local database and the 3rd-party vendor.`
    );
    if (!confirmed) {
      return;
    }

    this.isBulkUpdating = true;
    rows.forEach(row => {
      row.status = 'updating';
      row.message = undefined;
    });

    const payload: EmployerCorrectionRequest[] = rows.map(row => ({
      serial: row.serial,
      tnumber: row.tnumber,
      incorrectSpelling: row.name,
      correctedSpelling: row.correctedSpelling,
    }));

    this.employerService.correctEmployers(payload).subscribe({
      next: (results: EmployerCorrectionResult[]) => {
        rows.forEach(row => {
          const result = results.find(r => r.serial === row.serial && r.tnumber === row.tnumber);
          if (result) {
            this.applyResultToRow(row, result);
          } else {
            row.status = 'failed';
            row.message = 'No result returned for this row';
          }
        });
        this.isBulkUpdating = false;
      },
      error: (err) => {
        rows.forEach(row => this.applyErrorToRow(row, err));
        this.isBulkUpdating = false;
      },
    });
  }

  private applyResultToRow(row: EmployerCorrectionRow, result: EmployerCorrectionResult): void {
    row.status = result.success ? 'success' : 'failed';
    row.message = result.message;
    if (result.success) {
      row.name = result.correctedSpelling;
      row.correctedSpelling = result.correctedSpelling;
      row.selected = false;
    }
  }

  private applyErrorToRow(row: EmployerCorrectionRow, err: unknown): void {
    row.status = 'failed';
    row.message = err instanceof HttpErrorResponse
      ? `Failed to save employer name (HTTP ${err.status})`
      : 'Failed to save employer name: network error';
  }

  /** Downloads a blank CSV template matching the expected upload format. */
  downloadCsvTemplate(): void {
    const blob = new Blob([`${CSV_TEMPLATE_HEADER}\n`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'employer-correction-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  private loadEmployers(): void {
    this.isLoading = true;
    this.errorMessage = null;

    const employers$ = this.loadMode === 'serial'
      ? this.employerService.findEmployerBySerial(this.loadValue.trim())
      : this.employerService.findEmployersByName(this.loadValue.trim());

    employers$.subscribe({
      next: (response: Employer[]) => {
        this.rows = (response || []).map(employer => ({
          ...employer,
          correctedSpelling: employer.name,
          status: 'unmodified',
          selected: false,
        }));
        this.isLoading = false;
      },
      error: (err) => {
        this.rows = [];
        if (err instanceof HttpErrorResponse) {
          this.errorMessage = `Failed to load employers (HTTP ${err.status})`;
        } else {
          this.errorMessage = 'Failed to load employers: network error';
        }
        this.isLoading = false;
      },
    });
  }
}
